import { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap, revealUp } from '../lib/motion';

const SERVICES = [
  {
    title: 'General Contracting',
    copy: 'Ground-up builds, project management, scheduling, budgeting & permits.',
    img: '/images/header.jpg',
    to: '/services/general-contracting',
  },
  {
    title: 'Renovations & Remodeling',
    copy: 'Full interior remodels, kitchen & bath renovations, unit turns & build-outs.',
    img: '/images/renata-1.jpg',
    to: '/services/multifamily-renovation',
  },
  {
    title: 'Interior Finish-Out',
    copy: 'Drywall, LVP, tile & carpet, cabinets, trim, doors, and hardware.',
    img: '/images/renata-kitchen-1.jpg',
    to: '/services/multifamily-renovation',
  },
  {
    title: 'Structural Repairs',
    copy: 'Beam & post replacement, floor leveling, wood & metal framing.',
    img: '/images/planet-2.jpg',
    to: '/services/general-contracting',
  },
  {
    title: 'Exterior & Roofing',
    copy: 'Siding, roofing & waterproofing, windows, doors, and concrete flatwork.',
    img: '/images/roof-2.jpg',
    to: '/services/exterior-construction',
  },
  {
    title: 'Demolition & Haul-Off',
    copy: 'Selective & full gut to studs, concrete demo, debris haul-off & disposal.',
    img: '/images/aerial.jpg',
    to: '/services/demolition-haul-off',
  },
  {
    title: 'Make-Ready & Maintenance',
    copy: 'Make-readies, punch lists, preventative maintenance, minor MEP & drywall.',
    img: '/images/renata-gym-1.jpg',
    to: '/services/apartment-make-ready',
  },
  {
    title: 'Restoration & Steel',
    copy: 'Storm, fire & water restoration, seal coatings, steel stairs & rails.',
    img: '/images/roof-3.jpg',
    to: '/services/restoration',
  },
  {
    title: 'Cleaning Services',
    copy: 'Post-construction cleans, make-ready & common area, pressure washing.',
    img: '/images/renata-2.jpg',
    to: '/services/cleaning-services',
  },
  {
    title: 'Lawn & Landscape',
    copy: 'Mowing, edging & trimming, mulch & planting, ornamental care.',
    img: '/images/renata-ext-1.jpg',
    to: '/services/landscaping-grounds',
  },
  {
    title: 'Seasonal & Irrigation',
    copy: 'Seasonal cleanups, irrigation repairs & adjustments, storm debris removal.',
    img: '/images/renata-ext-4.jpg',
    to: '/services/landscaping-grounds',
  },
  {
    title: 'Tree Services',
    copy: 'Removal, trimming & pruning, stump grinding, hazard assessment.',
    img: '/images/renata-ext-6.jpg',
    to: '/services/tree-services',
  },
];

export default function Services() {
  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      revealUp('.svc-head', el);
      revealUp('.svc-card', el, {
        scrollTrigger: { trigger: el.querySelector('.svc-grid'), start: 'top 82%' },
        stagger: 0.08,
      });
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} id="services" className="relative bg-ink-950 py-28 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="svc-head mb-16 md:mb-24">
          <p className="eyebrow mb-6">Services</p>
          <h2 className="font-display text-4xl font-medium leading-tight text-steel-100 md:text-6xl">
            One crew. <span className="italic text-gold-300">Every trade.</span>
          </h2>
        </div>

        <div className="svc-grid grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Link
              key={s.title}
              to={s.to}
              className="svc-card group relative block overflow-hidden rounded-2xl border border-steel-100/[0.07] bg-ink-800/60 p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold-500/40 hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)]"
            >
              {/* cinematic image reveal on hover */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-0 transition-all duration-700 ease-out scale-110 group-hover:scale-100 group-hover:opacity-25"
                style={{ backgroundImage: `url("${s.img}")` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 via-transparent to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />

              <div className="relative z-10 flex h-full min-h-[220px] flex-col">
                <span className="font-display text-sm italic text-gold-400/80">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-4 font-display text-xl font-medium text-steel-100 md:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-steel-300">{s.copy}</p>
                <div className="mt-auto pt-6">
                  <div className="h-px w-8 bg-gold-500/50 transition-all duration-500 group-hover:w-full" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
